import React, { useEffect, useMemo, useState } from 'react'
import { Box, Paper, Tabs, Typography, useTheme, Tab, Button, Skeleton, Stack, Chip, useMediaQuery } from '@mui/material';
import { makeStyles } from '@mui/styles';
import KenTable from '../../components/KenTable';
import Header from '../../components/navigation/Header';
import { evaluationData } from '../../utils/apiService';
import { useNavigate } from 'react-router-dom';
import { formatDisplayDate } from '../../utils/dateUtils';
import NoDataImage from '../../assets/MyExams/NoData.png';


const useStyles = makeStyles(() => ({
    header: {
        fontSize: '18px',
        fontWeight: '500',
    },
    container: {
        borderRadius: '8px',
        marginTop: '32px',
        minHeight: 'calc(100vh - 165px)',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#fff'
    },
    tabRoot: {
        textTransform: 'capitalize', // 👈 make labels capitalized
        fontSize: '16px',
        fontWeight: 500,
    },
    mobileContainer: {
        borderRadius: 0,
        marginTop: '16px',
        padding: '0 16px 32px',
        backgroundColor: '#F5F6FA',
        minHeight: 'calc(100vh - 120px)'
    },
    mobileCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        boxShadow: '0px 10px 30px rgba(15, 23, 42, 0.06)',
    },
    mobileCardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    mobileCardTitle: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#0F172A'
    },
    mobileCardMeta: {
        fontSize: '13px',
        color: '#475467'
    },
    mobileStatusChip: {
        fontSize: '12px',
        fontWeight: 600,
        borderRadius: '999px',
        padding: '4px 10px',
    },
    mobileButton: {
        marginTop: '6px',
        textTransform: 'none',
        borderRadius: '10px',
        fontWeight: 600
    }
}));

// Custom TabPanel
interface TabPanelProps {
    children?: React.ReactNode;
    value: string;
    index: string;
}
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div hidden={value !== index}>
            {value === index && <Box sx={{ p: { xs: 0, sm: 2 } }}>{children}</Box>}
        </div>
    );
};

const Evaluation = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [value, setValue] = useState('one');
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };

    useEffect(() => {
        setLoading(true);
        evaluationData().then((res) => {
            const response = res?.data;

            const tempArray = response?.map((item: any) => ({
                id: item?.UniqueBundleNumber,
                title: item?.CourseName,
                courseCode: item?.CourseCode,
                bundleCode: item?.UniqueBundleNumber,
                papers: item?.examConnectionCount,
                format: item?.ExamFormat,
                status: item?.status,
                dueDate: formatDisplayDate(item?.BundleSubmittedOn),
                semester: item?.Semester,
                submittedOn: formatDisplayDate(item?.BundleSubmittedOn),
                ExamType: item?.ExamType,
                ExamFormat: item?.ExamFormat,
                evaluationType: item?.evaluationType,
                AssignedBundleID: item?.AssignedBundleID
            })) ?? [];

            setRows(tempArray);
        }).catch((err) => {
            console.error("Error in fetching evaluation data:", err);
        }).finally(() => setLoading(false));
    }, []);
    const columns = [
        { field: "bundleCode", headerName: "Bundle code" },
        { field: "title", headerName: "Course name" },
        { field: "courseCode", headerName: "Course code" },
        { field: "format", headerName: "Format" },
        { field: "semester", headerName: "Semester" },
        { field: "papers", headerName: "Total number of papers" },
        { field: "dueDate", headerName: "Due date" },
        {
            field: "action",
            headerName: "",
            renderCell: ({ value, row }: any) => (
                <Button
                    variant='secondary'
                    onClick={() => navigate('/faculty/evaluationRequests', {
                        state: {
                            data: row
                        },
                    })}
                >
                    Evaluate
                </Button>
            ),
        },
    ];


    const columns1 = [
        { field: "bundleCode", headerName: "Bundle code" },
        { field: "title", headerName: "Course" },
        { field: "courseCode", headerName: "Course code" },
        { field: "format", headerName: "Format" },
        { field: "semester", headerName: "Semester" },
        { field: "papers", headerName: "Total number of papers" },
        { field: "submittedOn", headerName: "Submitted on" },
    ];

    const assignedRows = useMemo(
        () => rows.filter((item) => item.status !== "Completed"),
        [rows]
    );

    const completedRows = useMemo(
        () => rows.filter((item) => item.status === "Completed"),
        [rows]
    );

    const renderEmptyState = (subtitle: string) => (
        <Box
            sx={{
                py: 6,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                color: '#6B7280',
                gap: 2
            }}
        >
            <img src={NoDataImage} alt="No data" style={{ maxWidth: 220, width: '80%', height: 'auto' }} />
            <Box>
                <Typography fontWeight={700} color="#111827">No Data</Typography>
                <Typography>{subtitle}</Typography>
            </Box>
        </Box>
    );

    const statusChipStyles: Record<string, { background: string; color: string }> = {
        "Submitted": { background: "#E6F4EA", color: "#1E7D34" },
        "Assigned": { background: "#F3F4F6", color: "#1F2933" },
        "Pending": { background: "#FFF4E5", color: "#B76E00" },
        "In progress": { background: "#E0EAFF", color: "#1D4ED8" },
    };

    const renderMobileCards = (data: any[], type: "assigned" | "completed") => {
        if (!data.length) {
            return renderEmptyState(
                type === "assigned"
                    ? "You have no evaluation requests."
                    : "You have no submitted evaluation requests."
            );
        }

        return (
            <Stack spacing={2} mt={2}>
                {data.map((bundle) => {
                    const chipStyle = statusChipStyles[bundle?.status] || { background: "#F3F4F6", color: "#111827" };
                    const isCompleted = type === "completed" || bundle?.status === "Submitted";
                    const dueLabel = isCompleted ? `Submitted on ${bundle?.submittedOn || "--"}` : `Due on ${bundle?.dueDate || "--"}`;
                    return (
                        <Paper key={bundle?.id} className={classes.mobileCard}>
                            <Box className={classes.mobileCardHeader}>
                                <Box>
                                    <Typography className={classes.mobileCardTitle}>{bundle?.title}</Typography>
                                    <Typography className={classes.mobileCardMeta}>
                                        {bundle?.courseCode} • Sem {bundle?.semester || "--"}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={bundle?.status || "Assigned"}
                                    className={classes.mobileStatusChip}
                                    sx={{ backgroundColor: chipStyle.background, color: chipStyle.color }}
                                />
                            </Box>
                            <Typography className={classes.mobileCardMeta}>
                                Bundle code: <strong>{bundle?.bundleCode}</strong>
                            </Typography>
                            <Typography className={classes.mobileCardMeta}>
                                Papers: {bundle?.papers || "--"} • Format: {bundle?.format || "--"}
                            </Typography>
                            <Typography className={classes.mobileCardMeta}>{dueLabel}</Typography>
                            <Button
                                fullWidth
                                variant={isCompleted ? "outlined" : "contained"}
                                color={isCompleted ? "inherit" : "primary"}
                                className={classes.mobileButton}
                                disabled={isCompleted}
                                onClick={() =>
                                    !isCompleted &&
                                    navigate('/faculty/evaluationRequests', {
                                        state: {
                                            data: bundle,
                                        },
                                    })
                                }
                            >
                                {isCompleted ? "Submitted" : "Evaluate"}
                            </Button>
                        </Paper>
                    );
                })}
            </Stack>
        );
    };

    const renderDesktopSkeleton = () => (
        <Box>
            <Header title='Evaluation Requests' />
            <Box className={classes.container}>
                <Box sx={{ width: '100%', borderBottom: '1px solid #E5E7EB', px: 3, py: 2 }}>
                    <Skeleton variant="rounded" width={200} height={36} />
                </Box>
                <Box sx={{ p: 3 }}>
                    <Stack spacing={2}>
                        {Array.from({ length: 5 }).map((_, idx) => (
                            <Paper key={idx} sx={{ p: 2 }}>
                                <Skeleton variant="text" width="60%" height={24} />
                                <Skeleton variant="text" width="45%" height={20} />
                                <Skeleton variant="rectangular" height={32} sx={{ mt: 1, borderRadius: 1 }} />
                            </Paper>
                        ))}
                    </Stack>
                </Box>
            </Box>
        </Box>
    );

    const renderMobileSkeleton = () => (
        <Box>
            <Header title='Evaluation Requests' />
            <Box className={classes.mobileContainer}>
                <Skeleton variant="text" width="40%" height={28} />
                <Stack spacing={2} mt={2}>
                    {Array.from({ length: 3 }).map((_, idx) => (
                        <Paper key={idx} className={classes.mobileCard}>
                            <Skeleton variant="text" width="60%" />
                            <Skeleton variant="text" width="40%" />
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
                        </Paper>
                    ))}
                </Stack>
            </Box>
        </Box>
    );

    if (loading) {
        return isMobile ? renderMobileSkeleton() : renderDesktopSkeleton();
    }


    return (
        <Box>
            <Header title='Evaluation Requests' />

            <Box className={isMobile ? classes.mobileContainer : classes.container}>
                <Box sx={{ width: '100%', backgroundColor: isMobile ? 'transparent' : '#fff' }}>
                    <Tabs
                        value={value}
                        onChange={handleChange}
                        textColor="secondary"
                        indicatorColor="secondary"
                        aria-label="exam tabs"
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                    >
                        <Tab sx={{ textTransform: 'capitalize' }} classes={{ root: classes.tabRoot }} value="one" label="Assigned" />
                        <Tab sx={{ textTransform: 'capitalize' }} classes={{ root: classes.tabRoot }} value="two" label="Completed" />
                    </Tabs>
                </Box>

                {/* Tab Panels */}
                <TabPanel value={value} index="one">
                    {isMobile ? renderMobileCards(assignedRows, "assigned") : (
                        assignedRows.length > 0 ? (
                            <KenTable columns={columns} rows={assignedRows} />
                        ) : (
                            renderEmptyState("You have no evaluation requests.")
                        )
                    )}
                </TabPanel>
                <TabPanel value={value} index="two">
                    {isMobile ? renderMobileCards(completedRows, "completed") : (
                        completedRows.length > 0 ? (
                            <KenTable columns={columns1} rows={completedRows} />
                        ) : (
                            renderEmptyState("You have no submitted evaluation requests.")
                        )
                    )}
                </TabPanel>

            </Box>
        </Box>
    );
};

export default Evaluation;

